import { sanitizeHtmlWithoutFontSizes } from 'utils'
import { checkDiscrepancies } from 'utils/debug/textSizesDiscrepancies'
import { EventCategory, logger } from 'utils/logger'
import { TEXT_MEASURE_ATTR } from '@/utils/editor/textGeometryAttributes'
import { paintedContentHeight, withoutBogus } from '@/utils/editor/textBoxPaint'
import { withoutActiveHtml } from '@/utils/tinyMCE/withoutActiveHtml'
import { GRACE_DISTANCE } from '@/utils/editor/textBoxLoadHeight'

const MODIFIER_PERCENTAGE = 1
const SMALLEST_FONT_ALLOWED = 8

/**
 * Fits the box's text into `internalWidth` × `internalHeight` by rescaling
 * every font size and line height in its HTML, one per cent a step, until
 * it fits or the 8 pt floor is reached (`content-overflowing`). With
 * `strict` the loop itself has no grace: content past the height by a pixel
 * is shrunk, which is what the load result asks for once it has decided the
 * text runs past the row's frame by more than `GRACE_DISTANCE`.
 */
export const fitTextIntoBox = (props) => {
  const {
    internalWidth,
    internalHeight,
    setItemState,
    TinyMCERef,
    applyText,
    resizingState,
    editorTextDimensions,
    environment,
    orderId,
    pageSetId,
    onMeasuredAtOne,
  } = props

  // The loop measures beside the painted body; without one there is nothing
  // to measure like, and nothing to fit.
  const body = TinyMCERef.current?.bodyElement
  if (!body) return

  setItemState('loading')

  try {
    const textContent = TinyMCERef.current.getContent()
    const contentWidth = body.offsetWidth
    // The body's height without TinyMCE's own scaffolding, which never
    // prints: the height the same HTML paints on every other surface.
    const contentHeight = paintedContentHeight(body)
    const content = { width: contentWidth, height: contentHeight }

    const wrapper = { width: internalWidth, height: internalHeight }
    const internal = { width: internalWidth, height: internalHeight }
    const sizes = { content, wrapper, internal, body }

    // The load result asks for the proof that the measurer lays out like the
    // paint: the same HTML at its authored sizes must measure the painted
    // body's height. Nothing else pays for that measurement.
    let measuredAtOne = null
    if (typeof onMeasuredAtOne === 'function') {
      measuredAtOne = measureTextDimensions({ html: textContent, body })
      onMeasuredAtOne({
        measured: measuredAtOne.height,
        painted: contentHeight,
      })
    }

    const resizeStrategyProps = { ...props, sizes }
    const resizeStrategy = determineResizeStrategy(resizeStrategyProps)

    const isWithinGraceBoundaries = resizeStrategy === 'within-grace-boundaries'
    const isGrowing = resizeStrategy === 'increase-font-size'
    const isIdle = resizingState === 'idle'
    const shouldNotGrow = isGrowing && isIdle
    const shouldNotAutosize = isWithinGraceBoundaries || shouldNotGrow

    const isPdfEnvironment = environment === 'pdf'

    if (isPdfEnvironment) {
      verifySizeDiscrepancies({
        internalWidth,
        internalHeight,
        contentWidth,
        contentHeight,
        textContent,
        editorTextDimensions,
        resizeStrategy,
        orderId,
        pageSetId,
      })
    }

    if (shouldNotAutosize) {
      setItemState('idle')
      return
    }

    const initialContent = TinyMCERef.current.getContent()
    const isInitialContentEmpty = initialContent.trim() === ''

    if (isInitialContentEmpty) {
      setItemState('idle')
      return
    }

    const initialDimensions =
      measuredAtOne && initialContent === textContent
        ? measuredAtOne
        : measureTextDimensions({ html: initialContent, body })

    const updateHTMLProps = {
      ...props,
      initialContent,
      initialDimensions,
      resizeStrategy,
      sizes,
      iteration: 1,
      currentContent: initialContent,
    }

    resizeText(updateHTMLProps)
  } catch (error) {
    console.error('Error autosizing text', error)
    setItemState('idle')
    applyText(TinyMCERef.current.getContent())
  }
}

const determineResizeStrategy = (props) => {
  const { sizes, strict = false } = props
  const { content, wrapper } = sizes

  if (strict) {
    const contentIsLargerThanWrapper =
      content.width > wrapper.width || content.height > wrapper.height
    return contentIsLargerThanWrapper
      ? 'decrease-font-size'
      : 'within-grace-boundaries'
  }

  // determine if the content is within the grace boundaries
  const widthDifference = Math.abs(content.width - wrapper.width)
  const heightDifference = Math.abs(content.height - wrapper.height)

  const widthWithinGraceBoundaries = widthDifference < GRACE_DISTANCE
  const heightWithinGraceBoundaries = heightDifference < GRACE_DISTANCE

  const withinGraceBoundaries =
    heightWithinGraceBoundaries && widthWithinGraceBoundaries

  if (withinGraceBoundaries) return 'within-grace-boundaries'

  // determine if the content is larger or smaller than the wrapper
  const contentWidthIsLarger = content.width > wrapper.width
  const contentHeightIsLarger = content.height > wrapper.height

  const contentIsLargerThanWrapper =
    contentWidthIsLarger || contentHeightIsLarger

  if (contentIsLargerThanWrapper) return 'decrease-font-size'

  // determine if the content is smaller than the wrapper
  const wrapperWidthIsLarger = content.width < wrapper.width
  const wrapperHeightIsLarger = content.height < wrapper.height

  const contentIsSmallerThanWrapper =
    wrapperWidthIsLarger || wrapperHeightIsLarger

  if (contentIsSmallerThanWrapper) return 'increase-font-size'

  // if none of the above conditions are met, return unknown
  return 'unknown'
}

const verifySizeDiscrepancies = (props) => {
  const {
    internalWidth,
    internalHeight,
    contentWidth,
    contentHeight,
    textContent,
    editorTextDimensions = {},
    resizeStrategy,
    orderId,
    pageSetId,
  } = props

  const hasEditorDimensions = Object.keys(editorTextDimensions).length > 0

  const currentTextDimensions = {
    currentWidth: internalWidth,
    currentHeight: internalHeight,
    currentContentWidth: contentWidth,
    currentContentHeight: contentHeight,
    currentText: textContent,
  }

  if (!hasEditorDimensions) return

  const hasDiscrepancies = checkDiscrepancies({
    editorTextDimensions,
    currentTextDimensions,
  })

  if (hasDiscrepancies) {
    logger.debug({
      message: 'text_discrepancy',
      category: EventCategory.DISCREPANCY,
      data: {
        ...editorTextDimensions,
        ...currentTextDimensions,
        resizeStrategy,
        orderId,
        pageSetId,
      },
    })
  }
}

const resizeText = (props) => {
  const { resizeStrategy, sizes, applyText, setItemState } = props
  const { currentContent, iteration } = props
  const { wrapper, body } = sizes

  const emergencyStop = iteration >= 1000
  if (emergencyStop) {
    setItemState('idle')
    applyText(currentContent)
    return
  }

  const isShrinking = resizeStrategy === 'decrease-font-size'
  const isGrowing = resizeStrategy === 'increase-font-size'

  const adjustFontSizesProps = {
    html: currentContent,
    type: resizeStrategy,
    modifier: iteration,
  }

  const adjustedFontSizes = updateHtml(adjustFontSizesProps)
  const { updatedHtml: resizedContent, smallestFontSize } = adjustedFontSizes

  const resizedDimensions = measureTextDimensions({
    html: resizedContent,
    body,
  })

  const reachedSmallestFontSize = smallestFontSize <= SMALLEST_FONT_ALLOWED
  const isOverflowing = reachedSmallestFontSize && isShrinking

  if (isOverflowing) {
    setItemState('content-overflowing')
    applyText(currentContent)
    return
  }

  if (isGrowing) {
    const contentWidthIsLarger = resizedDimensions.width > wrapper.width
    const contentHeightIsLarger = resizedDimensions.height > wrapper.height

    const contentIsLargerThanWrapper =
      contentWidthIsLarger || contentHeightIsLarger

    if (contentIsLargerThanWrapper) {
      applyText(currentContent)
      setItemState('idle')
      return
    }
  }

  if (isShrinking) {
    const contentWidthIsSmaller = resizedDimensions.width <= wrapper.width
    const contentHeightIsSmaller = resizedDimensions.height <= wrapper.height
    const contentIsSmallerThanWrapper =
      contentWidthIsSmaller && contentHeightIsSmaller

    if (contentIsSmallerThanWrapper) {
      applyText(resizedContent)
      setItemState('idle')
      return
    }
  }

  const nextIteration = iteration + 1
  const updateHTMLProps = {
    ...props,
    currentContent: resizedContent,
    iteration: nextIteration,
  }

  resizeText(updateHTMLProps)
}

/**
 * Lays `html` out the way the box paints it and reads its size. The
 * measurer is a shallow clone of the painted body element — its classes,
 * so the one global rule (`styles/tinyMCEStyles.ts`: base family and size,
 * `line-height: 1`, `padding: 0 2px`, `overflow-wrap`) styles both, and
 * its own inline style, with the id and the editing attributes stripped —
 * mounted beside the body in the box's own container, so every inherited
 * style matches too, and exactly as wide as the body paints. A word wider
 * than the box overflows both the same way, and the reading takes that
 * overflow in, so the loop still shrinks a long word until it fits the
 * width. The body's own subtree is never touched: its observers never see
 * a measurement.
 */
export const measureTextDimensions = ({ html, body }) => {
  if (!html || !body?.parentElement) return { width: 0, height: 0 }

  const container = body.cloneNode(false)
  container.removeAttribute('id')
  container.removeAttribute('contenteditable')
  container.removeAttribute('spellcheck')
  container.removeAttribute('aria-label')
  container.removeAttribute('tabindex')
  container.setAttribute(TEXT_MEASURE_ATTR, 'true')
  container.style.cssText = [
    container.style.cssText,
    'position: absolute',
    'top: 0',
    'left: 0',
    'visibility: hidden',
    'pointer-events: none',
    'box-sizing: border-box',
    `width: ${body.offsetWidth}px`,
    'height: auto',
    'min-height: 0',
    'max-height: none',
  ]
    .filter(Boolean)
    .join('; ')
  container.innerHTML = withoutActiveHtml(withoutBogus(html))
  body.parentElement.appendChild(container)

  // Height is the box's own height, like the paint's `offsetHeight`: under
  // `line-height: 1` a large glyph's content area overflows its line box,
  // and `scrollHeight` would count that overflow while the painted box does
  // not (measured 1.17× on a 120pt line). Width takes the scroll extent in:
  // a word wider than the box is real overflow the loop must shrink.
  const measured = {
    width: Math.max(container.offsetWidth, container.scrollWidth),
    height: container.offsetHeight,
  }
  container.remove()

  return measured
}

export const updateHtml = (props) => {
  const { type, modifer = 1 } = props
  let { html } = props

  const isGrowing = type === 'increase-font-size'
  const isShrinking = type === 'decrease-font-size'
  const isUnknown = !isGrowing && !isShrinking

  if (isUnknown) {
    const errorMessage = `Type must be either "increase-font-size" or "decrease-font-size"`
    throw new Error(errorMessage)
  }

  const increasePercentage = 1 + (MODIFIER_PERCENTAGE * modifer) / 100
  const decreasePercentage = 1 - (MODIFIER_PERCENTAGE * modifer) / 100

  const multiplier = isGrowing ? increasePercentage : decreasePercentage

  const fontSizeRegex = /font-size\s*:\s*([\d.]+)pt/gi
  const lineHeightRegex = /line-height\s*:\s*([\d.]+)([a-zA-Z%]*)/gi
  const matches = [...html.matchAll(fontSizeRegex)]

  const noMatchesFound = matches.length === 0
  if (noMatchesFound) html = sanitizeHtmlWithoutFontSizes(html)

  const fontSizes = matches.map((match) => parseFloat(match[1]))
  const smallestFontSize = fontSizes.length ? Math.min(...fontSizes) : null

  let updatedHtml = html.replace(fontSizeRegex, (fullMatch, numStr) => {
    const newValue = (parseFloat(numStr) * multiplier).toFixed(2)
    return `font-size: ${newValue}pt`
  })

  updatedHtml = updatedHtml.replace(
    lineHeightRegex,
    (fullMatch, numStr, unit) => {
      const newValue = (parseFloat(numStr) * multiplier).toFixed(2)
      return `line-height: ${newValue}${unit}`
    },
  )

  return { updatedHtml, smallestFontSize }
}

export const useResizeObserver = (props) => {
  const { resizingState } = props

  if (!resizingState) return
  if (resizingState === 'stop') fitTextIntoBox(props)
}
